import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import apiClient from "../../utils/axiosPetition";
import { alertaCargandoProceso, alertaGeneral, alertaToastTopEnd } from "../../utils/alertas";
import { getVideoAccessStatus } from '../../utils/appointmentAccessWindow';
import { useDataUser } from "../../context/dataUserContext";
import SvgIcon from "../../utils/Icon";
import moment from "moment-timezone";
import 'moment/locale/es';
import { parseAppointmentDate } from "../../utils/appointmentDate";
import { ReactComponent as Teleconsulta } from "../../assets/icons/agendar.svg";
import { ReactComponent as InfoPaciente } from "../../assets/icons/nombre.svg";
import { ReactComponent as Documento } from "../../assets/icons/documento.svg";
import { ReactComponent as Reloj } from "../../assets/icons/hora.svg";
import { ReactComponent as Fecha } from "../../assets/icons/calendario.svg";
import { ReactComponent as Citas } from "../../assets/icons/citas.svg";
import Modal from 'react-modal';
import Cargando from "../../utils/Cargando";
import { formatServiceName } from "../../utils/formatServiceName";
Modal.setAppElement('#root'); // Para accesibilidad del modal

const GestionCitas = ({ doctorId }) => {
    moment.locale('es');
    const [allAppointments, setAllAppointments] = useState([]);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [newAppointmentIds, setNewAppointmentIds] = useState([]);
    const { role } = useDataUser();
    const navigate = useNavigate();

    // Función para obtener las IDs de citas vistas desde localStorage
    const getViewedAppointments = () => {
        const key = `viewedAppointments_${doctorId}`;
        const viewed = localStorage.getItem(key);
        return viewed ? JSON.parse(viewed) : [];
    };

    // Función para guardar las IDs de citas vistas en localStorage
    const saveViewedAppointments = (appointmentIds) => {
        const key = `viewedAppointments_${doctorId}`;
        localStorage.setItem(key, JSON.stringify(appointmentIds));
    };

    // Función para detectar nuevas citas y mostrar notificación
    const checkForNewAppointments = async (newAppointments, previousAppointmentIds) => {
        const newAppointmentIds = newAppointments.map(app => app._id);
        const newIds = newAppointmentIds.filter(id => !previousAppointmentIds.includes(id));

        if (newIds.length > 0) {
            const newAppointmentsList = newAppointments.filter(app => newIds.includes(app._id));
            
            // Si hay múltiples nuevas citas, mostrar una notificación con todas
            if (newAppointmentsList.length === 1) {
                const appointment = newAppointmentsList[0];
                const patientName = appointment.patient?.fullName || 'Paciente';
                const appointmentDate = appointment.date.format('DD/MM/YYYY');
                const appointmentTime = appointment.date.format('hh:mm A');
                
                await alertaGeneral({
                    titulo: 'Nueva cita agendada',
                    messageHtml: `
                        <div style="text-align: left;">
                            <p><strong>Paciente:</strong> ${patientName}</p>
                            <p><strong>Fecha:</strong> ${appointmentDate}</p>
                            <p><strong>Hora:</strong> ${appointmentTime}</p>
                            ${appointment.notes ? `<p><strong>Notas:</strong> ${appointment.notes}</p>` : ''}
                        </div>
                    `,
                    isInfo: true,
                    confirmButtonText: 'Ver citas'
                });
            } else {
                // Múltiples nuevas citas
                const appointmentsList = newAppointmentsList.map(app => {
                    const patientName = app.patient?.fullName || 'Paciente';
                    const appointmentDate = app.date.format('DD/MM/YYYY');
                    const appointmentTime = app.date.format('hh:mm A');
                    return `<li style="margin-bottom: 10px;">
                        <strong>${patientName}</strong><br/>
                        ${appointmentDate} a las ${appointmentTime}
                    </li>`;
                }).join('');
                
                await alertaGeneral({
                    titulo: `${newAppointmentsList.length} nuevas citas agendadas`,
                    messageHtml: `
                        <div style="text-align: left;">
                            <ul style="list-style: none; padding: 0;">
                                ${appointmentsList}
                            </ul>
                        </div>
                    `,
                    isInfo: true,
                    confirmButtonText: 'Ver citas'
                });
            }
            
            // NO actualizamos las citas vistas aquí - las dejamos marcadas como nuevas
            // hasta que el usuario las vea explícitamente
        }
    };

    // Función para marcar una cita como vista (cuando el usuario la ve)
    const markAppointmentAsViewed = (appointmentId) => {
        setNewAppointmentIds(prev => prev.filter(id => id !== appointmentId));
        const viewedIds = getViewedAppointments();
        if (!viewedIds.includes(appointmentId)) {
            saveViewedAppointments([...viewedIds, appointmentId]);
        }
    };

    const fetchAllAppointments = useCallback(async (isPolling = false) => {
        try {
            if (!doctorId) {
                throw new Error('Falta información de autenticación');
            }

            const response = await apiClient.get(`/api/clinic/getAllAppointmentsDoctor/${doctorId}`);

            const allAppointmentsReturn = response.data.map(app => {
                return {
                    ...app,
                    patient: {
                        ...app.patient,
                        fullName: app.patient ? `${app.patient.firstName || ''} ${app.patient.firstLastName || ''}` : 'Paciente no disponible',
                    },
                    service: app.service.replace(/\d+/g, '').replace(/_/g, ' '),
                    date: parseAppointmentDate(app.date),
                }
            });

            // Identificar citas nuevas
            const previousAppointmentIds = getViewedAppointments();
            const currentAppointmentIds = allAppointmentsReturn.map(app => app._id);
            const newIds = currentAppointmentIds.filter(id => !previousAppointmentIds.includes(id));
            
            // Si es polling, verificar nuevas citas y mostrar notificación
            if (isPolling) {
                await checkForNewAppointments(allAppointmentsReturn, previousAppointmentIds);
                // Mantener las citas nuevas marcadas (no las marcamos como vistas hasta que el usuario las vea)
                setNewAppointmentIds(prev => {
                    // Combinar las nuevas citas detectadas con las que ya estaban marcadas
                    // Solo mantener las que aún existen en las citas actuales
                    const combined = [...new Set([...prev, ...newIds])];
                    return combined.filter(id => currentAppointmentIds.includes(id));
                });
            } else {
                // En la primera carga, guardar todas las citas como vistas
                saveViewedAppointments(currentAppointmentIds);
                // No hay citas nuevas en la primera carga
                setNewAppointmentIds([]);
            }

            setAllAppointments(allAppointmentsReturn);
            setCargando(false);
        } catch (error) {
            console.error('Error fetching all appointments:', error);
            setCargando(false);
        }
    }, [doctorId]);

    useEffect(() => {
        fetchAllAppointments();
    }, []);

    // Polling para verificar nuevas citas cada 30 segundos
    useEffect(() => {
        if (!doctorId || cargando) return;

        const intervalId = setInterval(() => {
            fetchAllAppointments(true);
        }, 30000); // Verificar cada 30 segundos

        return () => clearInterval(intervalId);
    }, [doctorId, cargando, fetchAllAppointments]);


    const handleVideoCall = (appointmentId, appointmentDate) => {
        const access = getVideoAccessStatus(appointmentDate?.toISOString?.() || appointmentDate);
        if (!access.canAccess) {
            alertaGeneral({
                titulo: access.code === 'TOO_EARLY' ? 'Aún no es hora de la consulta' : 'Videoconsulta no disponible',
                messageHtml: access.message,
                isWarning: true,
            });
            return;
        }
        navigate(`/video-call/${appointmentId}`);
    };

    const handleCancel = (appointmentId) => {
        setSelectedAppointment(appointmentId);
        setShowConfirmationModal(true);
    };

    const cancelAppointment = () => {
        alertaCargandoProceso({
            titulo: 'Cancelando cita...',
            messageHtml: 'Por favor, espera un momento',
            funcionAsync: () => {
                apiClient.put(`/api/clinic/cancelAppointment/${selectedAppointment}`, {
                    motivoCancelacion: cancelReason,
                    canceladoPor: role,
                }).then((res) => {
                    fetchAllAppointments();
                    setCancelReason('');
                    setShowReasonModal(false);

                    alertaToastTopEnd({
                        mensaje: 'Cita cancelada'
                    });
                }).catch((error) => {
                    alertaGeneral({
                        titulo: 'Ocurrió un error al cancelar la cita',
                        isError: true
                    })
                })

            }
        })

    };

    const ordenEstados = {
        "Agendada": 1,
        "Finalizada": 2,
        "Cancelada": 3
    }

    // Función para ordenar citas: primero por estado, luego por fecha/hora más cercana
    const sortAppointments = (appointments) => {
        const now = moment();
        
        return [...appointments].sort((a, b) => {
            // Primero ordenar por estado (Agendada primero)
            const estadoDiff = ordenEstados[a.estado] - ordenEstados[b.estado];
            if (estadoDiff !== 0) {
                return estadoDiff;
            }
            
            // Si tienen el mismo estado, ordenar por fecha/hora
            const dateA = moment(a.date);
            const dateB = moment(b.date);
            
            // Para citas agendadas, mostrar las más cercanas primero (ascendente)
            if (a.estado === 'Agendada' && b.estado === 'Agendada') {
                return dateA.diff(dateB);
            }
            
            // Para citas finalizadas o canceladas, mostrar las más recientes primero (descendente)
            return dateB.diff(dateA);
        });
    }

    const color = (estado) => {
        switch (estado.toLowerCase()) {
            case 'agendada':
                return 'bg-[#0aad5cff]';
            case 'cancelada':
                return 'bg-[#6e787aff]';
            case 'finalizada':
                return 'bg-[#205087ff]';
            default:
                return 'bg-[#0aad5cff]';
        }
    }

    if(cargando) {
        return <Cargando altura={"20vh"} />
    }

    return (
        <section className="rounded-xl overflow-hidden shadow-lg border border-[#6e787a]/20">
            <div className="flex items-center gap-3 bg-gradient-to-r from-[#1f94c2ff] to-[#205087ff] text-white px-5 py-2.5">
                <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-white/20 shrink-0">
                    <SvgIcon Icon={Citas} className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-sm md:text-base font-semibold tracking-tight leading-snug">Gestión de Citas</h2>
                    <p className="text-white/90 text-xs md:text-sm mt-0.5">
                        {allAppointments.length > 0
                            ? `${allAppointments.length} cita${allAppointments.length !== 1 ? 's' : ''} agendada${allAppointments.length !== 1 ? 's' : ''}`
                            : 'Revisa y gestiona tus citas'}
                    </p>
                </div>
            </div>
            <div className="bg-white border-l border-r border-b border-[#6e787a]/20">
                {allAppointments.length > 0 ? (
                <div className="grid gap-5 py-5 px-5 md:px-8 lg:grid-cols-3 2xl:grid-cols-4">
                    {sortAppointments(allAppointments).map((appointment, index) => {
                        const isNew = newAppointmentIds.includes(appointment._id);
                        return (
                        <div 
                            className={`flex flex-col h-full rounded-xl border relative transition-all duration-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 bg-white ${
                                isNew ? 'border-red-400 ring-2 ring-red-200/70' : 'border-slate-200'
                            }`} 
                            key={index}
                            onMouseEnter={() => {
                                // Cuando el usuario pasa el mouse sobre una cita nueva, marcarla como vista
                                if (isNew) {
                                    markAppointmentAsViewed(appointment._id);
                                }
                            }}
                        >
                            {isNew && (
                                <div className="absolute -top-2.5 -right-2.5 bg-red-500 text-white text-[9px] font-semibold rounded-full px-2.5 py-0.5 flex items-center gap-1 shadow-md z-10 animate-pulse">
                                    <span className="text-[10px]">●</span>
                                    <span>Nueva</span>
                                </div>
                            )}

                            <div className="px-4 pt-3.5 pb-2.5 border-b border-slate-100 flex items-start justify-between gap-3">
                                <div className="space-y-1">
                                    <p className="text-[11px] text-slate-500">
                                        {formatServiceName(appointment.service) || 'Consulta'}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-[15px] font-semibold text-slate-800 truncate max-w-[220px]">
                                            {appointment.patient?.fullName || 'Paciente no disponible'}
                                        </h3>
                                    </div>
                                </div>
                                <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm ${color(
                                        appointment.estado
                                    )}`}
                                >
                                    {appointment.estado}
                                </span>
                            </div>

                            <div className="px-4 py-2.5 space-y-1.5 text-[13px]">
                                <div className="flex items-center text-[#70706eff]">
                                    <SvgIcon Icon={Fecha} className={"w-5 h-5 text-[#1f94c2ff] mr-2 shrink-0"} />
                                    <span className="capitalize">{appointment.date.format('DD [] MMMM [] YYYY')}</span>
                                </div>

                                <div className="flex items-center text-[#70706eff]">
                                    <SvgIcon Icon={Reloj} className={"w-5 h-5 text-[#1f94c2ff] mr-2 shrink-0"} />
                                    <span>{appointment.date.format('hh:mm A')}</span>
                                </div>

                                {appointment.notes && (
                                    <div className="flex items-start text-[#70706eff]">
                                        <SvgIcon Icon={Documento} className={"w-5 h-5 text-[#1f94c2ff] mr-2 mt-0.5 shrink-0"} />
                                        <p className="text-[12px] leading-snug line-clamp-2">
                                            {appointment.notes}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Aparecerá sólo cuando el estado sea agendada */}
                            {appointment.estado?.toLowerCase() === 'agendada' && (
                                <div className="px-4 pb-3.5 pt-1 mt-auto">
                                    <div className="flex flex-col sm:flex-row gap-2.5">
                                        {/* Botón iniciar videoconsulta */}
                                        <button 
                                            className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#1f94c2ff] to-[#1b7ea6ff] text-white px-3.5 py-2.5 rounded-lg focus:outline-none shadow-md hover:shadow-lg hover:from-[#1b7ea6ff] hover:to-[#1f94c2ff] transition-all duration-150 text-sm font-semibold" 
                                            onClick={() => handleVideoCall(appointment._id, appointment.date)}
                                        >
                                            <SvgIcon Icon={Teleconsulta} className="w-4 h-4" />
                                            <span>Ir a la videoconsulta</span>
                                        </button>
                                        {/* Boton cancelar videoconsulta */}
                                        <button 
                                            className="sm:w-auto inline-flex justify-center items-center px-3.5 py-2.5 text-sm font-semibold text-[#6e787aff] bg-white border border-[#c3c7cb] rounded-lg hover:bg-slate-50 transition-all duration-150" 
                                            onClick={() => handleCancel(appointment._id)}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}

                        </div>
                    );
                    })}
                </div>
                ) : (
                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-[#1f94c2ff]/10 text-[#1f94c2ff] mb-4">
                        <SvgIcon Icon={Citas} className="w-12 h-12" />
                    </div>
                    <p className="text-[#6e787aff] font-medium text-lg">No hay citas agendadas</p>
                    <p className="text-[#6e787aff]/80 text-sm mt-1 max-w-sm">Cuando un paciente agende una cita contigo, aparecerá aquí.</p>
                </div>
                )}
            </div>
            {/* Modales para caso de cancelación cita */}
            <Modal
                isOpen={showConfirmationModal}
                onRequestClose={() => setShowConfirmationModal(false)}
                className="w-full max-w-[340px] mx-auto bg-white p-6 rounded-lg shadow-lg text-center border border-[#6e787aff] focus:outline-none"
                overlayClassName="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75"
            >
                <h2 className="text-lg text-[#70706eff] font-semibold mb-4">
                    ¿Estás seguro de que quieres cancelar esta cita?
                </h2>
                <div className="flex justify-center space-x-4">
                    <button onClick={() => setShowConfirmationModal(false)} className="w-full bg-[#70706eff] text-white px-3 py-1 rounded-md border border-[#6e787aff] focus:outline-none">
                        No
                    </button>
                    <button onClick={() => { setShowConfirmationModal(false); setShowReasonModal(true) }} className="w-full bg-red-600 text-white px-3 py-1 rounded-md border border-[#6e787aff] focus:outline-none">
                        Sí
                    </button>
                </div>
            </Modal>

            <Modal
                isOpen={showReasonModal}
                onRequestClose={() => setShowReasonModal(false)}
                className="w-full max-w-[340px] mx-auto bg-white p-6 rounded-lg shadow-lg text-center border border-[#6e787aff] focus:outline-none"
                overlayClassName="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75"
            >
                <h2 className="text-lg text-left text-[#70706eff] font-semibold mb-3">
                    Por favor, ingresa el motivo de la cancelación:
                </h2>
                <textarea
                    placeholder="Motivo de la cancelación"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="border border-[#6e787aff] rounded w-full focus:outline-none mb-3 p-2"
                />
                <div className="flex justify-center space-x-4">
                    <button onClick={() => setShowReasonModal(false)} className="w-full bg-[#70706eff] text-white px-3 py-1 rounded-md border border-[#6e787aff] focus:outline-none">
                        Cancelar
                    </button>
                    <button onClick={cancelAppointment} className="w-full bg-[#1f94c2ff] text-white px-3 py-1 rounded-md border border-[#6e787aff] focus:outline-none">
                        Confirmar
                    </button>
                </div>
            </Modal >
        </section >
    );
}

export default GestionCitas;